import { v4 as uuidv4 } from 'uuid';
import { PAYMENT_PLAN_TYPES } from '../constants';
import { requirePortalEventsConfig } from '../config/portalEventsConfig';
import {
  accountView,
  paymentPlanView,
  selectedReferenceNumberView,
  sessionView,
  shortLinkRedirectView,
} from '../state/views';
import { debugConsole, redactUrlForTelemetry } from '../utils';
import { captureCampaignAttribution } from '../utils/campaignAttribution';
import { getSessionId } from '../utils/mixpanel';
import {
  PARTIAL_PAYMENT_SELECTOR_EXPERIMENT,
  getActiveExperimentAssignment,
} from './experiments';

export const PORTAL_EVENT_TYPES = {
  landed: 'LANDED',
  firstContentfulPaint: 'FIRST_CONTENTFUL_PAINT',
  pageLoaded: 'PAGE_LOADED',
  experimentExposed: 'EXPERIMENT_EXPOSED',
  loginSucceeded: 'LOGIN_SUCCEEDED',
  loginFailed: 'LOGIN_FAILED',
  paymentMethodSelected: 'PAYMENT_METHOD_SELECTED',
  paymentDetailsEntered: 'PAYMENT_DETAILS_ENTERED',
  paymentPlanDetailsEntered: 'PAYMENT_PLAN_DETAILS_ENTERED',
  paymentAuthorizationSubmitted: 'PAYMENT_AUTHORIZATION_SUBMITTED',
  paymentAuthorized: 'PAYMENT_AUTHORIZED',
  paymentMade: 'PAYMENT_MADE',
  planCreated: 'PLAN_CREATED',
  goodFaithOfferSelected: 'GOOD_FAITH_OFFER_SELECTED',
  postLoginExperienceLanded: 'POST_LOGIN_EXPERIENCE_LANDED',
  postLoginExperienceFallback: 'POST_LOGIN_EXPERIENCE_FALLBACK',
};

// Payment conversion events link to the partial-payment selector assignment so
// funnel metrics can be reported by variant. Login events keep debt-login.
const PARTIAL_PAYMENT_LINKED_EVENT_TYPES = new Set([
  PORTAL_EVENT_TYPES.paymentMethodSelected,
  PORTAL_EVENT_TYPES.paymentDetailsEntered,
  PORTAL_EVENT_TYPES.paymentPlanDetailsEntered,
  PORTAL_EVENT_TYPES.paymentAuthorizationSubmitted,
  PORTAL_EVENT_TYPES.paymentAuthorized,
  PORTAL_EVENT_TYPES.paymentMade,
  PORTAL_EVENT_TYPES.planCreated,
]);

export const PORTAL_LOGIN_FAILURE_REASONS = {
  invalidCredentials: 'INVALID_CREDENTIALS',
  requestFailed: 'REQUEST_FAILED',
  missingAuthResponse: 'MISSING_AUTH_RESPONSE',
  networkError: 'NETWORK_ERROR',
  serverError: 'SERVER_ERROR',
  timeout: 'TIMEOUT',
  expiredLink: 'EXPIRED_LINK',
};

const INVALID_CREDENTIAL_ERROR_TYPES = new Set([
  'INVALID_CREDENTIALS',
  'LEGACY_VERIFY_IDENTITY_FAILED',
]);

export const COOKIE_CONSENT_STATUSES = {
  pending: 'PENDING',
  accepted: 'ACCEPTED',
  rejected: 'REJECTED',
};

const PORTAL_SESSION_STARTED_AT_KEY = 'portalSessionStartedAt';
const PORTAL_SESSION_STARTED_SESSION_ID_KEY = 'portalSessionStartedSessionId';
const LOGIN_FAILURE_REASON_VALUES = new Set(
  Object.values(PORTAL_LOGIN_FAILURE_REASONS)
);
const MAX_FAILURE_REASON_DETAIL_LENGTH = 500;

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage || null;
};

const getOrCreateSessionId = () => {
  const existingSessionId = getSessionId();
  if (existingSessionId) return existingSessionId;

  const fallbackSessionId = uuidv4();
  try {
    getStorage()?.setItem('sessionId', fallbackSessionId);
  } catch (_error) {}
  return fallbackSessionId;
};

const getOrCreateSessionStartedAt = (sessionId) => {
  const storage = getStorage();
  if (!storage) return new Date().toISOString();

  const existingStartedAt = storage.getItem(PORTAL_SESSION_STARTED_AT_KEY);
  const existingStartedSessionId = storage.getItem(
    PORTAL_SESSION_STARTED_SESSION_ID_KEY,
  );
  if (existingStartedAt && existingStartedSessionId === sessionId) {
    return existingStartedAt;
  }

  const startedAt = new Date().toISOString();
  try {
    storage.setItem(PORTAL_SESSION_STARTED_AT_KEY, startedAt);
    storage.setItem(PORTAL_SESSION_STARTED_SESSION_ID_KEY, sessionId);
  } catch (_error) {}
  return startedAt;
};

const getCurrentUrl = () =>
  typeof window === 'undefined'
    ? undefined
    : redactUrlForTelemetry(window.location.href);

const normalizeString = (value) => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const normalizeLoginFailureReason = (value) => {
  const normalized = normalizeString(value)
    ?.toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (!normalized) return null;
  return LOGIN_FAILURE_REASON_VALUES.has(normalized)
    ? normalized
    : PORTAL_LOGIN_FAILURE_REASONS.requestFailed;
};

const sanitizeFailureReasonDetail = (value) => {
  const normalized = normalizeString(value);
  if (!normalized) return null;
  return normalized
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .slice(0, MAX_FAILURE_REASON_DETAIL_LENGTH);
};

const getErrorStatus = (error) => {
  const status = Number(error?.response?.status || 0);
  return Number.isFinite(status) && status > 0 ? status : null;
};

export const classifyPortalLoginFailure = (error, { defaultReason, detail } = {}) => {
  const status = getErrorStatus(error);
  const statusType = status ? Math.floor(status / 100) : 0;
  const errorType = normalizeString(error?.response?.data?.error?.type);
  const code = normalizeString(error?.code);
  const message = normalizeString(error?.message);
  let failureReason = defaultReason || PORTAL_LOGIN_FAILURE_REASONS.requestFailed;

  if (INVALID_CREDENTIAL_ERROR_TYPES.has(errorType)) {
    failureReason = PORTAL_LOGIN_FAILURE_REASONS.invalidCredentials;
  } else if (statusType === 5) {
    failureReason = PORTAL_LOGIN_FAILURE_REASONS.serverError;
  } else if (status === 401) {
    failureReason = PORTAL_LOGIN_FAILURE_REASONS.invalidCredentials;
  } else if (status === 410) {
    failureReason = PORTAL_LOGIN_FAILURE_REASONS.expiredLink;
  } else if (
    code === 'ECONNABORTED' ||
    error?.name === 'AbortError' ||
    /timeout/i.test(message || '')
  ) {
    failureReason = PORTAL_LOGIN_FAILURE_REASONS.timeout;
  } else if (!error?.response && (error?.request || code === 'ERR_NETWORK')) {
    failureReason = PORTAL_LOGIN_FAILURE_REASONS.networkError;
  }

  const detailParts = [
    detail,
    status ? `status=${status}` : null,
    errorType ? `error_type=${errorType}` : null,
    code ? `code=${code}` : null,
  ].filter(Boolean);

  return {
    failureReason: normalizeLoginFailureReason(failureReason),
    failureReasonDetail: sanitizeFailureReasonDetail(detailParts.join(', ')),
  };
};

const getUrlDebtIdentifier = (currentUrl) => {
  if (!currentUrl) return null;

  try {
    const parsedUrl = new URL(currentUrl);
    const queryDebtIdentifier =
      normalizeString(parsedUrl.searchParams.get('debtID')) ||
      normalizeString(parsedUrl.searchParams.get('debtId')) ||
      normalizeString(parsedUrl.searchParams.get('referenceNumber'));
    if (queryDebtIdentifier) return queryDebtIdentifier;

    const routeMatch = parsedUrl.pathname.match(
      /\/(?:special-offer|payment-plan|pay-now)\/user\/([^/?#]+)/
    );
    return routeMatch ? decodeURIComponent(routeMatch[1]) : null;
  } catch (_error) {
    return null;
  }
};

const getStateDebtIdentifier = () => {
  const shortLink = shortLinkRedirectView.read().state || {};
  const account = accountView.read().state || {};

  return (
    normalizeString(shortLink.referenceNumber) ||
    normalizeString(selectedReferenceNumberView.read().state) ||
    normalizeString(account.referenceNumber) ||
    normalizeString(account.accountId)
  );
};

const getDebtIdentifier = (currentUrl) =>
  getUrlDebtIdentifier(currentUrl) || getStateDebtIdentifier();

const parseJsonSafely = async (response) => {
  const rawText = await response.text().catch(() => '');
  if (!rawText) return {};
  try {
    return JSON.parse(rawText);
  } catch (_error) {
    return {};
  }
};

const getLinkableExperimentAssignmentToken = (assignment) => {
  if (
    assignment?.participating !== true ||
    !assignment?.experimentRunId ||
    !assignment?.assignmentToken
  ) {
    return '';
  }
  return normalizeString(assignment.assignmentToken);
};

const buildEventPayload = ({
  eventType,
  debtIdentifier,
  failureReason,
  failureReasonDetail,
  utmContent,
  experienceKey,
  fallbackReason,
  experimentAssignmentToken,
  currentUrl = getCurrentUrl(),
}) => {
  const redactedCurrentUrl = redactUrlForTelemetry(currentUrl);
  const attribution =
    captureCampaignAttribution({ url: redactedCurrentUrl }) || {};
  const sessionId = getOrCreateSessionId();
  const resolvedDebtIdentifier =
    normalizeString(debtIdentifier) || getDebtIdentifier(redactedCurrentUrl);
  const normalizedFailureReason =
    eventType === PORTAL_EVENT_TYPES.loginFailed
      ? normalizeLoginFailureReason(failureReason)
      : null;
  const normalizedFailureReasonDetail =
    eventType === PORTAL_EVENT_TYPES.loginFailed
      ? sanitizeFailureReasonDetail(failureReasonDetail)
      : null;
  const isPartialPaymentFlow =
    paymentPlanView.read().state?.planType === PAYMENT_PLAN_TYPES.paymentPartial;
  const partialPaymentAssignmentToken =
    isPartialPaymentFlow && PARTIAL_PAYMENT_LINKED_EVENT_TYPES.has(eventType)
    ? getLinkableExperimentAssignmentToken(
        getActiveExperimentAssignment(PARTIAL_PAYMENT_SELECTOR_EXPERIMENT)
      )
    : '';
  const resolvedAssignmentToken =
    normalizeString(experimentAssignmentToken) ||
    partialPaymentAssignmentToken;

  return {
    portalEventId: uuidv4(),
    eventType,
    effectiveAt: new Date().toISOString(),
    currentUrl: redactedCurrentUrl,
    ...(attribution.trace_identifier
      ? { traceIdentifier: attribution.trace_identifier }
      : {}),
    ...(resolvedDebtIdentifier ? { debtIdentifier: resolvedDebtIdentifier } : {}),
    ...(normalizeString(utmContent) ? { utmContent: normalizeString(utmContent) } : {}),
    ...(normalizeString(experienceKey) ? { experienceKey: normalizeString(experienceKey) } : {}),
    ...(normalizeString(fallbackReason) ? { fallbackReason: normalizeString(fallbackReason) } : {}),
    ...(normalizedFailureReason ? { failureReason: normalizedFailureReason } : {}),
    ...(normalizedFailureReasonDetail
      ? { failureReasonDetail: normalizedFailureReasonDetail }
      : {}),
    ...(resolvedAssignmentToken
      ? { experimentAssignmentToken: resolvedAssignmentToken }
      : {}),
    session: {
      sessionId,
      startedAt: getOrCreateSessionStartedAt(sessionId),
    },
  };
};

const buildPrivacySafeEventPayload = ({
  eventType,
  experimentAssignmentToken,
}) => {
  const sessionId = getOrCreateSessionId();
  const assignmentToken = normalizeString(experimentAssignmentToken);

  return {
    portalEventId: uuidv4(),
    eventType,
    effectiveAt: new Date().toISOString(),
    ...(assignmentToken
      ? { experimentAssignmentToken: assignmentToken }
      : {}),
    session: {
      sessionId,
      startedAt: getOrCreateSessionStartedAt(sessionId),
    },
  };
};

const buildCookieConsentPayload = ({
  status,
  currentUrl = getCurrentUrl(),
}) => {
  const redactedCurrentUrl = redactUrlForTelemetry(currentUrl);
  const attribution =
    captureCampaignAttribution({ url: redactedCurrentUrl }) || {};
  const traceIdentifier = attribution.trace_identifier;
  const debtIdentifier = getDebtIdentifier(redactedCurrentUrl);

  if (!traceIdentifier && !debtIdentifier) return null;

  const sessionId = getOrCreateSessionId();

  return {
    status,
    effectiveAt: new Date().toISOString(),
    ...(traceIdentifier ? { traceIdentifier } : {}),
    ...(debtIdentifier ? { debtIdentifier } : {}),
    session: {
      sessionId,
      startedAt: getOrCreateSessionStartedAt(sessionId),
    },
  };
};

const buildChannelPreferencePayload = ({
  preferences,
  debtIdentifier,
  currentUrl = getCurrentUrl(),
}) => {
  const redactedCurrentUrl = redactUrlForTelemetry(currentUrl);
  const resolvedDebtIdentifier =
    normalizeString(debtIdentifier) || getDebtIdentifier(redactedCurrentUrl);
  if (!resolvedDebtIdentifier) return null;

  return {
    debtIdentifier: resolvedDebtIdentifier,
    accountNotificationsPreferred:
      Boolean(preferences?.communicationTypes?.accountNotifications),
    marketingCommunicationsPreferred:
      Boolean(preferences?.communicationTypes?.marketingCommunications),
    channelEmailPreferred: Boolean(preferences?.preferredChannels?.email),
    channelSmsPreferred: Boolean(preferences?.preferredChannels?.sms),
    channelPhonePreferred: Boolean(preferences?.preferredChannels?.phone),
    channelPhysicalMailPreferred:
      Boolean(preferences?.preferredChannels?.physicalMail),
    source: 'PAYMENT_PORTAL',
  };
};

export const classifyPaymentPortalEventType = (paymentPlan = {}) => {
  const isSinglePayment =
    Number(paymentPlan.numPayments) === 1 ||
    [
      PAYMENT_PLAN_TYPES.payInFull,
      PAYMENT_PLAN_TYPES.paymentPartial,
      PAYMENT_PLAN_TYPES.settleInFull,
    ].includes(paymentPlan.planType);

  return isSinglePayment
    ? PORTAL_EVENT_TYPES.paymentMade
    : PORTAL_EVENT_TYPES.planCreated;
};

export const preparePortalEventPayload = (
  event,
  { privacySafe = false } = {},
) =>
  privacySafe
    ? buildPrivacySafeEventPayload(event)
    : buildEventPayload(event);

export const recordPortalEvent = async (
  event,
  { privacySafe = false, preparedPayload } = {},
) => {
  const config = requirePortalEventsConfig();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeoutMs);
  const authToken = sessionView.read().state?.authToken;
  const payload =
    preparedPayload ||
    (privacySafe
      ? buildPrivacySafeEventPayload(event)
      : buildEventPayload(event));

  try {
    const response = await fetch(config.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.API_KEY ? { 'x-api-key': process.env.API_KEY } : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    const responsePayload = await parseJsonSafely(response);

    if (!response.ok) {
      const error = new Error('Portal event persistence failed.');
      error.status = response.status;
      error.payload = responsePayload;
      throw error;
    }

    return responsePayload;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const recordCookieConsent = async ({ status, currentUrl } = {}) => {
  const payload = buildCookieConsentPayload({ status, currentUrl });
  if (!payload) return { skipped: true, reason: 'missing_portal_session_link' };

  const config = requirePortalEventsConfig();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeoutMs);
  const authToken = sessionView.read().state?.authToken;

  try {
    const response = await fetch(`${config.baseUrl}/cookie-consent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.API_KEY ? { 'x-api-key': process.env.API_KEY } : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    const responsePayload = await parseJsonSafely(response);

    if (!response.ok) {
      const error = new Error('Cookie consent persistence failed.');
      error.status = response.status;
      error.payload = responsePayload;
      throw error;
    }

    return responsePayload;
  } catch (error) {
    debugConsole.warn('Cookie consent persistence failed', {
      status,
      message: error.message,
      statusCode: error.status,
    });
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getChannelPreferences = async ({ debtIdentifier, currentUrl } = {}) => {
  const resolvedDebtIdentifier =
    normalizeString(debtIdentifier) || getDebtIdentifier(currentUrl || getCurrentUrl());
  if (!resolvedDebtIdentifier) {
    return { skipped: true, reason: 'missing_debt_identifier' };
  }

  const config = requirePortalEventsConfig();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeoutMs);
  const authToken = sessionView.read().state?.authToken;
  const query = new URLSearchParams({ debtIdentifier: resolvedDebtIdentifier });

  try {
    const response = await fetch(`${config.baseUrl}/channel-preferences?${query}`, {
      method: 'GET',
      headers: {
        ...(process.env.API_KEY ? { 'x-api-key': process.env.API_KEY } : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      signal: controller.signal,
    });
    const payload = await parseJsonSafely(response);

    if (!response.ok) {
      const error = new Error('Channel preference lookup failed.');
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload?.data?.preference || null;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const recordChannelPreferences = async ({
  preferences,
  debtIdentifier,
  currentUrl,
} = {}) => {
  const payload = buildChannelPreferencePayload({
    preferences,
    debtIdentifier,
    currentUrl,
  });
  if (!payload) {
    throw new Error('Debt context is required to save communication preferences.');
  }

  const config = requirePortalEventsConfig();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeoutMs);
  const authToken = sessionView.read().state?.authToken;

  try {
    const response = await fetch(`${config.baseUrl}/channel-preferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.API_KEY ? { 'x-api-key': process.env.API_KEY } : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    const responsePayload = await parseJsonSafely(response);

    if (!response.ok) {
      const error = new Error('Channel preference persistence failed.');
      error.status = response.status;
      error.payload = responsePayload;
      throw error;
    }

    return responsePayload;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const recordPortalEventSafely = (event, options) => {
  return recordPortalEvent(event, options)
    .then(() => true)
    .catch((error) => {
      debugConsole.warn('Portal event persistence failed', {
        eventType: event?.eventType,
        status: error?.status || null,
        message: error?.message || null,
      });
      return false;
    });
};

export const recordLoginFailureEvent = ({
  debtIdentifier,
  failureReason = PORTAL_LOGIN_FAILURE_REASONS.requestFailed,
  failureReasonDetail,
  experimentAssignmentToken,
} = {}) => {
  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.loginFailed,
    ...(debtIdentifier ? { debtIdentifier } : {}),
    failureReason,
    failureReasonDetail,
    ...(experimentAssignmentToken ? { experimentAssignmentToken } : {}),
  });
};

export const recordPaymentMethodSelectedEvent = () => {
  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.paymentMethodSelected,
  });
};

export const recordPaymentDetailsEnteredEvent = () => {
  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.paymentDetailsEntered,
  });
};

export const recordPaymentPlanDetailsEnteredEvent = () => {
  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.paymentPlanDetailsEntered,
  });
};

export const recordPaymentAuthorizationSubmittedEvent = () => {
  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.paymentAuthorizationSubmitted,
  });
};

export const recordPaymentAuthorizedEvent = () => {
  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.paymentAuthorized,
  });
};

export const recordPaymentCreationEvent = ({ paymentPlan }) => {
  recordPortalEventSafely({
    eventType: classifyPaymentPortalEventType(paymentPlan),
  });
};

export const recordGoodFaithOfferSelectedEvent = ({
  debtIdentifier,
} = {}) => {
  if (!debtIdentifier) return;

  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.goodFaithOfferSelected,
    debtIdentifier,
  });
};

export const recordPostLoginExperienceLandedEvent = ({
  debtIdentifier,
  utmContent,
  experienceKey,
} = {}) => {
  if (!debtIdentifier || !experienceKey) return;

  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.postLoginExperienceLanded,
    debtIdentifier,
    utmContent,
    experienceKey,
  });
};

export const recordPostLoginExperienceFallbackEvent = ({
  debtIdentifier,
  utmContent,
  fallbackReason,
} = {}) => {
  if (!debtIdentifier) return;

  recordPortalEventSafely({
    eventType: PORTAL_EVENT_TYPES.postLoginExperienceFallback,
    debtIdentifier,
    utmContent,
    fallbackReason,
  });
};
